import clsx from "clsx";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { compactNumber } from "../util/format.js";
import styles from "./StatDelta.module.scss";

/**
 * How to read a delta's number.
 *
 * - **`percent`** (default) — a signed percent (150 → `+150%`). Right for an unbounded LEVEL, where the ratio
 *   is the natural reading of growth.
 * - **`absolute`** — the signed difference in the metric's own units, compact (2_100_000 → `+2.1m`, 3 → `+3`).
 *   Right for the two kinds of number a percent lies about: a running TOTAL, whose whole history sits in the
 *   denominator so a strong day reads as `+0.001%`; and a BOUNDED INDEX like Spotify's 0–100 popularity,
 *   which moves in points and has no meaningful ratio at all.
 *
 * The unit is a property of the DATUM, not of the surface — the server sends it alongside the value
 * (`HeroDelta`) so a tile cannot render a change in a unit the thing that computed it did not mean.
 */
export type StatDeltaUnit = "percent" | "absolute";

export interface StatDeltaProps {
  /** The signed change. Null/non-finite renders nothing — never a fabricated 0. Read per {@link unit}. */
  value: number | null | undefined;
  /** How to read {@link value} — see {@link StatDeltaUnit}. Default `percent`. */
  unit?: StatDeltaUnit;
  /**
   * Which surface this sits on.
   *
   * - `default` — the filled pill, on the status ramp's `-bg`/`-ink` pairs. For light cards.
   * - `inverse` — bare tinted text with no fill, for the DARK hero (`DarkHero` / `DarkStatTile`). The
   *   `-ink` colours are tuned for contrast against a light fill and go muddy on a near-black scrim, and a
   *   light `-bg` wash reads as a second surface floating on the hero — which is why the reference drops the
   *   fill entirely here and brightens the hue (`roster-shared.css:555`).
   *
   * The direction glyph, rounding, sign and the null contract are IDENTICAL across tones on purpose: this
   * stays one component so a delta cannot mean two different things depending on where it is drawn.
   */
  tone?: "default" | "inverse";
  className?: string;
}

/**
 * A signed delta chip (StatDelta) — up=success, down=danger, flat=neutral. The direction is carried by
 * an arrow glyph as well as color, so the sign is NEVER conveyed by color alone (a11y: secondary encoding).
 * Tabular numerals. A null delta renders nothing (the tile shows its value with no change chip).
 */
export function StatDelta({ value, unit = "percent", tone = "default", className }: StatDeltaProps) {
  if (value == null || !Number.isFinite(value)) return null;
  const dir = value > 0 ? "up" : value < 0 ? "down" : "flat";
  const Glyph = dir === "up" ? ArrowUpRight : dir === "down" ? ArrowDownRight : Minus;
  // `compactNumber` already carries the minus for a fall, so only a rise needs a sign added — otherwise a
  // drop renders `+-2.1m`. The percent lane rounds to 1dp exactly as it always did.
  const rounded = Math.round(value * 10) / 10;
  const body = unit === "absolute" ? compactNumber(value) : `${rounded}%`;
  const sign = value > 0 ? "+" : "";
  return (
    <span className={clsx(styles.delta, styles[dir], tone === "inverse" && styles.inverse, className)}>
      <Glyph className={styles.glyph} aria-hidden="true" />
      {sign}
      {body}
    </span>
  );
}
